import React, { Component } from 'react';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import lodashValues from 'lodash/fp/values';
import lodashCompact from 'lodash/fp/compact';
import queryString from 'query-string';

import {
  USER_ROLE_ADMIN,
  USER_ROLE_FIELD_OPS,
  USER_ROLE_PHOTOGRAPHER,
  hasRoleAdmin,
} from 'common/userRoles';

import {
  SR_API_ACTION_GET_LISTING_BY_ID,
  SR_API_ACTION_GET_REDIRECTION_BY_LISTING_ID,
  SR_API_ACTION_LISTING_JOB_ITEMS_INSTALLED,
} from 'api/actionTypes';

import { SCREEN_ID_ADMIN_SR_LISTING_EDIT_PAGE } from 'client/constants/screenIds';

import allowedRoles from 'client/helpers/allowedRoles';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import branch from 'client/core/branch';
import postWithState from 'client/actions/postWithState';
import getPartnerListAction from 'client/actions/Partner/getPartnerList';
import getMarketsListAction from 'client/actions/Market/getList';
import getSunlocksListAction from 'client/actions/Sunlock/getSunlocksList';
import getCodeboxListAction from 'client/actions/Codebox/getCodeboxList';
import getListingLinksAction from 'client/actions/Listing/getLinks';
import getApartmentListAction from 'client/actions/Apartment/getList';
import getPropertyAttributeListByListingId from 'client/actions/PropertyAttribute/getListByListingId';
import getFloorPlansListByApartmentId from 'client/actions/FloorPlan/getListByApartmentId';
import AccountManagerActions from 'client/actions/AccountManager';
import LeasingManagerActions from 'client/actions/LeasingManager';
import LandlordActions from 'client/actions/Landlord';
import PartnerActions from 'client/actions/Partner';
import PreLeaseActions from 'client/actions/PreLease';
import PartnerUserActions from 'client/actions/PartnerUser';

import SrEditListingForm, {
  SR_EDIT_LISTING_FORM_VIEW_GROUP_ALL,
  SR_EDIT_LISTING_FORM_VIEW_GROUP_FIELD_OPTS,
  SR_EDIT_LISTING_FORM_VIEW_GROUP_PHOTOGRAPHERS,
} from 'client/components/SrEditListingForm';
import SrPage from 'client/components/SrPage';
import log from 'client/lib/log';

@withRouter
@withTransitionHook
@allowedRoles({
  roles: [USER_ROLE_ADMIN, USER_ROLE_PHOTOGRAPHER, USER_ROLE_FIELD_OPS],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@branch(({ match }) => {
  const cursors = {
    currentUser: ['currentUser'],
    partnersList: ['partners', 'list'],
    marketsById: ['markets', 'byId'],
    sunlocksList: ['sunlocks', 'list'],
    codeboxLockList: ['codebox', 'list'],
    listingLinksByListingId: ['listingLinksByListingId'],
    apartmentsList: ['apartments', 'list'],
    propertyAttributesByListingId: ['propertyAttributes', 'listByListingId'],
    landlordsById: ['landlords', 'byId'],
    partnerUsersByPartnerId: ['partners', 'users', 'listByPartnerId'],
    marketingListingsByListingId: ['partners', 'marketingListingsByListingId'],
  };

  if (match.params.listingId) {
    cursors.accountManager = ['accountManagers', 'byListingId', match.params.listingId];
    cursors.preLease = ['preLeases', 'byListingId', match.params.listingId];
    cursors.leasingManager = ['leasingManagers', 'byListingId', match.params.listingId];
  }

  return cursors;
})
@withScreenId(SCREEN_ID_ADMIN_SR_LISTING_EDIT_PAGE)
@withScrollToTop
class AdminSrListingEditPage extends Component {
  static fetchData(match, inject) {
    const listingId = parseInt(match.params.listingId, 10);
    return Promise.all(
      lodashCompact([
        getPartnerListAction(inject),
        getMarketsListAction(inject),
        getSunlocksListAction(inject),
        getCodeboxListAction(inject),
        getApartmentListAction(inject),
        listingId && getListingLinksAction(inject, { listingId }),
        listingId && getPropertyAttributeListByListingId(inject, { listingId }),
        listingId && AccountManagerActions.getByListingId(inject, { listingId }),
        listingId && PreLeaseActions.getByListingId(inject, { listingId }),
        listingId && LeasingManagerActions.getByListingId(inject, { listingId }),
      ]),
    );
  }

  static propTypes = {
    match: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    partnersList: PropTypes.array,
    codeboxLockList: PropTypes.array,
    apartmentsList: PropTypes.array,
    marketsById: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    listingLinksByListingId: PropTypes.object.isRequired,
    propertyAttributesByListingId: PropTypes.object.isRequired,
    accountManager: PropTypes.object,
    landlordsById: PropTypes.object.isRequired,
    partnerUsersByPartnerId: PropTypes.object.isRequired,
    marketingListingsByListingId: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      listing: null,
      listingRedirection: null,
      listingRedirectionIsLoading: !!props.match.params.listingId,
      jobItemsInstalled: null,
    };
  }

  componentDidMount() {
    this._initializeListingEditForm();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.listingId !== this.props.match.params.listingId) {
      this._initializeListingEditForm();
    }
  }

  _initializeListingEditForm = () => {
    const {
      currentUser,
      partnersList,
      apartmentsList,
      match: { params },
      dispatch,
    } = this.props;

    const isAdmin = hasRoleAdmin(currentUser.roles);

    this.setState({
      listing: null,
      listingRedirection: null,
      listingRedirectionIsLoading: !!params.listingId,
      jobItemsInstalled: null,
    });

    const listingId = params.listingId ? parseInt(params.listingId, 10) : null;

    if (listingId) {
      dispatch(postWithState, {
        action: SR_API_ACTION_GET_LISTING_BY_ID,
        payload: {
          listingId: listingId,
        },
      })
        .then((listing) => {
          if (listing.listingManagementCompany && listing.apartmentName) {
            const partner =
              partnersList &&
              partnersList.find(({ managementCompanyName }) => {
                return listing.listingManagementCompany === managementCompanyName;
              });
            if (partner) {
              const apartment =
                apartmentsList &&
                apartmentsList.find(({ name }) => {
                  return name === listing.apartmentName;
                });
              if (apartment) {
                dispatch(getFloorPlansListByApartmentId, {
                  apartmentId: apartment.id,
                });
              }
            }
          }

          if (isAdmin && listing.landlordId) {
            dispatch(LandlordActions.getById, {
              landlordId: listing.landlordId,
            });
          }
          if (isAdmin && listing.partnerId) {
            dispatch(PartnerUserActions.getPartnerUserListByPartnerId, {
              partnerId: listing.partnerId,
            });
            dispatch(PartnerActions.getMarketingListingByListingId, {
              listingId: listing.id,
            });
          }

          this.setState({ listing });

          return Promise.all([
            dispatch(postWithState, {
              action: SR_API_ACTION_GET_REDIRECTION_BY_LISTING_ID,
              payload: {
                listingId: listingId,
              },
            }),
            dispatch(postWithState, {
              action: SR_API_ACTION_LISTING_JOB_ITEMS_INSTALLED,
              payload: {
                listingId: listingId,
              },
            }),
          ]);
        })
        .then(([listingRedirection, jobItemsInstalled]) => {
          this.setState({
            listingRedirection: listingRedirection,
            listingRedirectionIsLoading: false,
            jobItemsInstalled,
          });
        })
        .catch((error) => {
          log.error(error);
        });
    }
  };

  _onEditListingFormSubmit = (newListing) => {
    const { history, location, dispatch } = this.props;
    const { listing } = this.state;
    this.setState({
      listing: newListing,
      listingRedirection: null,
    });

    const locationQuery = queryString.parse(location.search);

    if (locationQuery.backUrl) {
      history.push(locationQuery.backUrl);
      return;
    }

    if (!listing) {
      history.push('/admin/listings');
    } else {
      dispatch(postWithState, {
        action: SR_API_ACTION_GET_REDIRECTION_BY_LISTING_ID,
        payload: {
          listingId: newListing.id,
        },
      }).then((listingRedirection) => {
        this.setState({
          listingRedirection: listingRedirection,
          listingRedirectionIsLoading: false,
        });
      });
    }
  };

  _getEditListingFormViewGroup = () => {
    const {
      location,
      route: { params },
    } = this.props;
    const locationQuery = queryString.parse(location.search);
    if (locationQuery.view === 'fieldOps') {
      return SR_EDIT_LISTING_FORM_VIEW_GROUP_FIELD_OPTS;
    }
    if (params?.isPhotographerRoute) {
      return SR_EDIT_LISTING_FORM_VIEW_GROUP_PHOTOGRAPHERS;
    }
    return SR_EDIT_LISTING_FORM_VIEW_GROUP_ALL;
  };

  render() {
    const {
      partnersList,
      marketsById,
      listingLinksByListingId,
      sunlocksList,
      codeboxLockList,
      propertyAttributesByListingId,
      apartmentsList,
      accountManager,
      leasingManager,
      landlordsById,
      partnerUsersByPartnerId,
      marketingListingsByListingId,
      dispatch,
      currentUser,
      location,
      match: { params },
      preLease,
    } = this.props;

    const {
      listing,
      listingRedirection,
      listingRedirectionIsLoading,
      jobItemsInstalled,
    } = this.state;

    const editListingFormViewGroup = this._getEditListingFormViewGroup();
    const listingIsLoading = params.listingId && !listing;

    const listingLinksListIsLoading =
      params.listingId && !listingLinksByListingId[params.listingId];

    const propertyAttributeListIsLoading =
      params.listingId && !propertyAttributesByListingId[params.listingId];

    const isAdmin = hasRoleAdmin(currentUser.roles);
    const landlordIsLoading =
      isAdmin && listing && listing.landlordId && !landlordsById[listing.landlordId];

    const shouldRenderLoadingGif =
      listingIsLoading ||
      !partnersList ||
      listingRedirectionIsLoading ||
      !sunlocksList ||
      !codeboxLockList ||
      listingLinksListIsLoading ||
      !apartmentsList ||
      propertyAttributeListIsLoading ||
      landlordIsLoading;

    const locationQuery = queryString.parse(location.search);

    return (
      <SrPage
        navbarProps={{
          backButtonLink: locationQuery.backUrl || null,
          redirectLogoTo: locationQuery.backUrl || '/admin/listings',
          disableRedirect: editListingFormViewGroup === SR_EDIT_LISTING_FORM_VIEW_GROUP_FIELD_OPTS,
        }}
      >
        <div className="width-90">
          {shouldRenderLoadingGif ? (
            <div className="loading-gif" />
          ) : (
            <SrEditListingForm
              formViewGroup={editListingFormViewGroup}
              listing={listing}
              listingRedirection={listingRedirection}
              jobItemsInstalled={jobItemsInstalled}
              partnersList={partnersList}
              marketsList={lodashValues(marketsById)}
              sunlocksList={sunlocksList}
              codeboxLockList={codeboxLockList}
              apartmentsList={apartmentsList}
              listingLinks={(listing && listingLinksByListingId[listing.id]) || []}
              propertyAttributes={(listing && propertyAttributesByListingId[listing.id]) || []}
              landlord={listing && landlordsById[listing.landlordId]}
              partnerUsers={(listing && partnerUsersByPartnerId[listing.partnerId]) || []}
              marketingPartnerListing={listing && marketingListingsByListingId[listing.id]}
              accountManager={accountManager}
              preLease={preLease}
              leasingManager={leasingManager}
              onSubmit={this._onEditListingFormSubmit}
              dispatch={dispatch}
            />
          )}
        </div>
      </SrPage>
    );
  }
}

AdminSrListingEditPage.displayName = 'AdminSrListingEditPage';

export default AdminSrListingEditPage;
